import {Platform} from 'react-native';
import Toast from 'react-native-toast-message';
import {AxiosError, isAxiosError} from 'axios';
import {makeAutoObservable, runInAction} from 'mobx';
import {makeLoggable} from 'mobx-log';

import {ERROR_MESSAGE, PASSWORD_RESET_REQUEST, SUCCESS_CHECK} from '../components/ToastBottom';
import {AppRoute} from '../models/AppRoute';
import {Address, User} from '../models/mfl';
import {AccountScreenAction, parseAccountScreenActionKey} from '../models/navigationTypes';
import {type NestedRouteParams, offerDetailRoute} from '../navigation/offerRoute';
import {navigate} from '../navigation/RootNavigation';
import apiService, {convertAppAddressToMFLAddress} from '../transport/apiService';
import type {SignUpResult} from '../transport/types';
import {maybeSeedE2eSessionFromLaunchUrl} from '../utils/e2eSessionSeed';
import {logError} from '../utils/errorLogging';
import {
  getValueFromStorage,
  removeValueFromStorage,
  setValueToStorage,
} from '../utils/localStorage';
import {logger, serializeError} from '../utils/logger';
import {getCreateOrUpdateUserErrorMessages, hasWeakPasswordError} from '../utils/mfl';
import {getMissingDetailFields} from '../utils/missingDetails';
import {logout as logoutOneSignal} from '../utils/onesignal';
import {publishSessionEvent} from '../utils/sessionSync';

import type {RootStore} from './rootStore';
import {computeSessionState, type SessionState} from './sessionState';

const SERVER_SESSION_ID = 'SERVER_SESSION_ID';

// SHORTCUT: OFFER_RETURN_TTL_MS (30 min) is a ceiling, not a measured join duration; the lite in-flight slice lives 5 min (DIS-1228). Upgrade path: measure join durations from offer_deeplink_state events and set the TTL from p99.
export const OFFER_RETURN_TTL_MS = 30 * 60 * 1000;

// Type definitions for API responses
interface EmailCheckResponse {
  can_sign_in: boolean;
  can_sign_up: boolean;
  exists_in_zen: boolean;
  first_name: string;
  status: string;
}

interface LoginResponse {
  needToVerifyEmail?: boolean;
  id: string;
}

interface CreateUserResponse {
  id: string;
  zohoId?: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  email_address: string;
  birth_date: string;
  billing_address: Address;
}

interface UserFormValues {
  email?: string;
  password?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  billingAddress?: Address;
  optInRewards?: boolean;
  signUpCode?: string;
  passwordValidationBypass?: boolean;
}

export class UserStore {
  rootStore: RootStore;
  // Session Detail (DIS-1026: server-side sessions)
  serverSessionId: string = '';
  isSessionChecked: boolean = false;
  // User detail
  id: string = ''; // MFL ID
  zohoId: string = '';
  firstName: string = '';
  lastName: string = '';
  phoneNumber: string = '';
  emailAddress: string = '';
  password: string = '';
  dateOfBirth?: string; // YYYY-MM-DD format
  billingAddress?: Address;
  // DIS-1891: true only after GET /ecommerce/v2/user succeeded. A failed fetch
  // leaves the profile empty, and isUserMissingDetails must not read that
  // empty profile as missing details.
  isUserInfoLoaded = false;
  showWeakPasswordCheckbox = false;
  postLoginReturnToScreen: AppRoute | [AppRoute, NestedRouteParams] | null = null;
  // DIS-1859: one-shot intent from a sign-up Success CTA with no store
  // selected. Find Your Store opens with `suburbSearch` in its input; once a
  // store is picked, Home consumes this and continues to sign-in. In memory
  // only: a reload drops it and the user lands Home with the store set.
  signInAfterStoreSelection: {suburbSearch: string} | null = null;
  // DIS-1917: one-shot intent to return to an offer after sign-in or join.
  // default() does not clear it: a lite sign-up ends in a forced sign-out
  // (REWARDS_SUCCESS), and logout() calls default(), so a cleared intent would
  // be gone before the success CTA reads it. Consumed only through
  // takeOfferReturn(); the offer screen reads it only to clear it on arrival.
  offerReturn: {offerId: string; setAt: number} | null = null;
  // DIS-1917: the offer whose "Sign in to view my offer" started the current
  // sign-in. Separate from offerReturn, which that offer clears on arrival and
  // the join flows also set. The offer screen alone reads and clears it.
  offerSignInFrom: string | null = null;
  formValues: UserFormValues | null = null;
  // Cached user data from password reset flow (for retry after weak password)
  private _passwordResetUserData: CreateUserResponse | null = null;

  /**
   * This will be set if user has checked opt in rewards when create account but zen account creation did not succeed
   */
  loyaltyPending: boolean = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
    makeLoggable(this);
  }

  get name() {
    return `${this.firstName} ${this.lastName}`;
  }

  reset = () => {};

  default = () => {
    runInAction(() => {
      this.serverSessionId = '';
      this.isSessionChecked = false;
      this.firstName = '';
      this.lastName = '';
      this.phoneNumber = '';
      this.emailAddress = '';
      this.password = '';
      this.dateOfBirth = undefined;
      this.billingAddress = undefined;
      this.isUserInfoLoaded = false;
      this.showWeakPasswordCheckbox = false;
      this.postLoginReturnToScreen = null;
      this.formValues = null;
      this._passwordResetUserData = null;
    });
  };

  get isLoggedIn(): boolean {
    if (Platform.OS === 'web') {
      return this.isSessionChecked && !!this.serverSessionId;
    }
    return !!this.serverSessionId;
  }

  /** DIS-1914: `pending` while the app is still working out `isLoggedIn`. */
  get sessionState(): SessionState {
    return computeSessionState({
      isAppInitialising: this.rootStore.globalStore.isAppInitialising,
      isLoggedIn: this.isLoggedIn,
    });
  }

  get isUserMissingDetails() {
    return getMissingDetailFields(this).length > 0;
  }

  setPostLoginReturnToScreen = (screen: AppRoute | null, params?: NestedRouteParams) => {
    if (params && !!screen) {
      this.postLoginReturnToScreen = [screen, params];
    } else {
      this.postLoginReturnToScreen = screen;
    }
  };

  setOfferReturn = (offerId: string) => {
    this.offerReturn = {offerId, setAt: Date.now()};
  };

  clearOfferReturn = () => {
    this.offerReturn = null;
  };

  setOfferSignInFrom = (offerId: string | null) => {
    this.offerSignInFrom = offerId;
  };

  takeOfferReturn = (): [AppRoute, NestedRouteParams] | null => {
    const intent = this.offerReturn;
    this.offerReturn = null;
    if (!intent || Date.now() - intent.setAt > OFFER_RETURN_TTL_MS) {
      return null;
    }
    return offerDetailRoute(intent.offerId);
  };

  setSignInAfterStoreSelection = (intent: {suburbSearch: string} | null) => {
    this.signInAfterStoreSelection = intent;
  };

  logout = async (options?: {callApi?: boolean; broadcast?: boolean}) => {
    const {callApi = true, broadcast = true} = options ?? {};

    // DIS-1915: every session logout lands here, including passive and
    // cross-tab (passiveLogout) and the 401 interceptor. Clear the external
    // id before anything else so a later throw still removes it.
    await logoutOneSignal();

    // Passive logout (another tab already logged out): the server session is
    // already dead — replaying POST /ecommerce/logout would just 401.
    if (callApi) {
      await apiService.logout();
    }

    await removeValueFromStorage(SERVER_SESSION_ID);

    this.default();

    // Generate a new device ID on logout, per MFL request.
    await this.rootStore.globalStore.resetDeviceId();

    await this.rootStore.logoutUser();

    if (broadcast) {
      publishSessionEvent({type: 'logout', at: Date.now()});
    }

    return null;
  };

  checkEmailNew = async (email: string) => {
    this.emailAddress = email;

    return apiService
      .checkEmail2(email)
      .then((data: EmailCheckResponse) => {
        return {
          canSignIn: data.can_sign_in,
          canSignUp: data.can_sign_up,
          existsInZen: data.exists_in_zen,
          firstName: data.first_name,
          status: data.status,
        };
      })
      .catch(err => logger.error('UserStore', 'Error checking email (v2)', serializeError(err)));
  };

  login = async (body: {email: string; password: string}) => {
    try {
      const data = (await apiService.login2(body)) as LoginResponse;
      logger.debug('UserStore', 'Login v2 successful', {
        email: body.email,
        needToVerifyEmail: data.needToVerifyEmail,
      });

      if (data.needToVerifyEmail) {
        // navigate to email verification screen

        runInAction(() => {
          this.emailAddress = body?.email;
          this.password = body?.password; // needed for /verify-email endpoint
        });

        navigate(AppRoute.ROOT, {
          screen: AppRoute.MAIN,
          params: {screen: AppRoute.ACCOUNT_EMAIL_VERIFICATION},
        });
      } else {
        // Session stored automatically by sessionIdExtractionInterceptor
        await this.rootStore.login();
        if (this.isUserInfoLoaded && this.isUserMissingDetails) {
          navigate(AppRoute.ROOT, {
            screen: AppRoute.MAIN,
            params: {screen: AppRoute.ACCOUNT_COLLECT_MISSING_DETAILS},
          });
          return false;
        }

        return true;
      }
    } catch (err) {
      if (err instanceof AxiosError) {
        // TODO surface error details to user
      } else {
        logError(err, 'UserStore.login');
      }
      return false;
    }
  };

  verifyUserEmail = async (code: string) => {
    try {
      const data = await apiService.verifyEmail(this.emailAddress, this.password, code);

      if (data) {
        // Session stored automatically by sessionIdExtractionInterceptor
        this.rootStore.invalidateLinesStores();
        await this.rootStore.initialiseUserData();

        if (this.isUserInfoLoaded && this.isUserMissingDetails) {
          navigate(AppRoute.ROOT, {
            screen: AppRoute.MAIN,
            params: {screen: AppRoute.ACCOUNT_COLLECT_MISSING_DETAILS},
          });
          return false;
        }

        return true;
      }
      return false;
    } catch (error) {
      logError(error, 'UserStore.verifyUserEmail');
      return false;
    }
  };

  afterLogin = (fallback?: AppRoute, options?: {useOfferReturn?: boolean}) => {
    const offerRoute =
      this.postLoginReturnToScreen || options?.useOfferReturn === false
        ? null
        : this.takeOfferReturn();
    if (this.postLoginReturnToScreen) {
      if (Array.isArray(this.postLoginReturnToScreen)) {
        navigate(...this.postLoginReturnToScreen);
      } else {
        navigate(this.postLoginReturnToScreen);
      }
      this.setPostLoginReturnToScreen(null);
    } else if (offerRoute) {
      navigate(...offerRoute);
    } else if (fallback) {
      navigate(fallback);
    } else {
      const navigationTarget = this.rootStore.shopSelectionStore?.homeAppRoute;
      navigate(navigationTarget);
    }
  };

  afterUpdateUser = () => {
    this.afterLogin(undefined, {useOfferReturn: false});
  };

  /**
   * Store the server-issued session ID received in x-session-id response header.
   * Called by sessionIdExtractionInterceptor (fire-and-forget from interceptor context).
   * MobX update is synchronous; EncryptedStorage write is async (native only).
   */
  storeServerSessionId = async (sessionId: string): Promise<void> => {
    runInAction(() => {
      this.serverSessionId = sessionId;
      this.isSessionChecked = true;
    });
    // A live session now exists — clear any prior dead-session logout latch so
    // a future session death can fire logout again. Session-death responses
    // carry no x-session-id, so this is never reached during a 401 burst.
    apiService.resetUnauthorizedGuard();
    if (Platform.OS !== 'web') {
      await setValueToStorage(SERVER_SESSION_ID, sessionId);
    }
    // Announce the new session to sibling tabs (web only — no-op on native).
    // Deliberately NOT published from restoreSession: cold-start restores
    // happen in every tab independently and would just add broadcast noise.
    publishSessionEvent({type: 'login', at: Date.now()});
  };

  /**
   * Restore session on app startup.
   * - Web: validates cookie via GET /ecommerce/session (cookie sent automatically)
   * - Native: reads SERVER_SESSION_ID from EncryptedStorage
   */
  restoreSession = async (): Promise<boolean> => {
    if (Platform.OS === 'web') {
      try {
        await apiService.checkSession();
        runInAction(() => {
          // Sentinel value — actual auth is the mw_session cookie.
          this.serverSessionId = 'web-session-active';
          this.isSessionChecked = true;
        });
        // Live session restored — clear any stale logout latch.
        apiService.resetUnauthorizedGuard();
        return true;
      } catch {
        runInAction(() => {
          this.serverSessionId = '';
          this.isSessionChecked = true;
        });
        return false;
      }
    }

    // Native: restore from EncryptedStorage
    await maybeSeedE2eSessionFromLaunchUrl();
    const sessionId = await getValueFromStorage(SERVER_SESSION_ID);
    runInAction(() => {
      this.serverSessionId = sessionId || '';
      this.isSessionChecked = true;
    });
    if (sessionId) {
      // Live session restored — clear any stale logout latch.
      apiService.resetUnauthorizedGuard();
    }
    return !!sessionId;
  };

  storeUserInfo = (data: {
    id: string; // MFL ID
    first_name: string;
    last_name: string;
    phone_number: string;
    email_address: string;
    birth_date: string;
    billing_address: Address;
    zohoId?: string;
  }) => {
    try {
      if (window?.$zoho?.salesiq?.visitor) {
        // Despite Zoho documentation which says we to use the .ready() method,
        // it doesn't work, so we ignore it and just set the values directly.
        window.$zoho.salesiq.visitor.email(data.email_address);
        window.$zoho.salesiq.visitor.contactnumber(data.phone_number);
        window.$zoho.salesiq.visitor.name(`${data.first_name} ${data.last_name}`);
      }
    } catch (error) {
      logError(error, 'UserStore.retrieveUserInfoV2.zohoIntegration');
    }
    runInAction(() => {
      this.id = data.id;
      this.firstName = data.first_name;
      this.lastName = data.last_name;
      this.phoneNumber = data.phone_number;
      this.emailAddress = data.email_address;
      this.dateOfBirth = data.birth_date; // Already in YYYY-MM-DD format
      this.billingAddress = data.billing_address;
      this.zohoId = data.zohoId || '';
    });
  };

  retrieveUserInfo = async () => {
    try {
      const data = await apiService.getUserInfo();
      logger.debug('UserStore', 'Retrieved user info', {
        userId: data?.id,
        email: data?.email,
      });
      if (data) {
        this.storeUserInfo(data);
        return data;
      }
      return null;
    } catch (error) {
      logger.error('UserStore', 'Unable to retrieve user info', {error});
      return null;
    }
  };

  retrieveUserInfoV2 = async (options?: {retryable?: boolean; zohoId?: string}) => {
    runInAction(() => {
      this.isUserInfoLoaded = false;
    });
    try {
      const user = await apiService.getUserInfoV2(options);
      this.storeUserInfo({
        id: user.id,
        zohoId: user.zohoId,
        first_name: user.firstName,
        last_name: user.lastName,
        phone_number: user.phoneNumber,
        email_address: user.email,
        birth_date: user.dateOfBirth, // Already converted to YYYY-MM-DD in transformer
        billing_address: user.billingAddress,
      });
      runInAction(() => {
        this.isUserInfoLoaded = true;
      });
    } catch (err) {
      logError(err, 'UserStore.retrieveUserInfoV2');
      throw err;
    }
  };

  handleCreateUpdateUserError = (err: unknown, isCreate: boolean = false) => {
    let errorMessage = `Unable to ${isCreate ? 'create' : 'update'} user. Please try again.`;
    let isWeakPassword = false;

    // Runtime validation: Check if error is an AxiosError
    if (!isAxiosError(err)) {
      logger.error('UserStore', 'Non-Axios error in user operation', serializeError(err));
      Toast.show({
        type: ERROR_MESSAGE,
        text1: errorMessage,
        visibilityTime: 8000,
      });
      return;
    }

    // Safe to access Axios-specific properties after type guard
    const errors = err.response?.data?.errors;
    const status = err.response?.data?.status;

    if (errors) {
      isWeakPassword = hasWeakPasswordError(errors);
      if (!isWeakPassword) {
        errorMessage = getCreateOrUpdateUserErrorMessages(errors);
      }
    }

    // Check for PASSWORD_TOO_WEAK status
    if (status === 'PASSWORD_TOO_WEAK') {
      isWeakPassword = true;
    }

    if (isWeakPassword) {
      runInAction(() => (this.showWeakPasswordCheckbox = true));
      errorMessage = 'This password is too weak or may be compromised';
    }

    Toast.show({
      type: ERROR_MESSAGE,
      text1: errorMessage,
      visibilityTime: 8000,
    });
  };

  updateUserInfo = async (
    action: keyof typeof AccountScreenAction | AccountScreenAction = 'UNKNOWN',
    values: User,
    successCallback = () => {},
    errorCallback: (error?: {status: string; message: string}) => void = () => {},
    options?: {
      showSuccessToast?: boolean;
      verificationCode?: string;
    },
  ) => {
    const {showSuccessToast = true, verificationCode} = options || {};
    try {
      runInAction(() => {
        this.showWeakPasswordCheckbox = false;
      });
      const actionString = parseAccountScreenActionKey(action) || 'UNKNOWN';
      const response = await apiService.updateUserInfo(actionString, values, {
        verificationCode,
      });
      this.storeUserInfo(response.data);
      if (showSuccessToast) {
        Toast.show({
          type: SUCCESS_CHECK,
          onHide: successCallback,
        });
      } else {
        successCallback();
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const e = error.response?.data;
        errorCallback({
          status: e?.status,
          message: e?.message,
        });
      } else {
        errorCallback();
      }
      this.handleCreateUpdateUserError(error);
    }
  };

  updateBillingAddress = async (address: Address) => {
    logger.debug('UserStore', 'Updating billing address', {
      postalCode: address.postal_code,
      locality: address.locality,
    });
    return new Promise<boolean>((resolve, reject) => {
      this.updateUserInfo(
        'UPDATE_BILLING_ADDRESS',
        {
          firstName: this.firstName,
          lastName: this.lastName,
          email: this.emailAddress,
          phoneNumber: this.phoneNumber,
          dateOfBirth: this.dateOfBirth,
          billingAddress: {...address},
        },
        () => resolve(true),
        () => reject(),
        {
          showSuccessToast: false,
        },
      );
    });
  };

  /** This create account is for the loyalty work, capturing the form data
   * while the user verifies their phone number.
   */
  updateUserFormValues = (values: UserFormValues) => {
    runInAction(() => {
      this.formValues = values;
    });
  };

  signUpUser = async (code: string): Promise<SignUpResult> => {
    const selectedStore = this.rootStore.shopSelectionStore.selectedStoreSummary;
    if (!selectedStore) {
      logger.error('UserStore', 'No selected store for sign up');
      return {ok: false, reason: 'no_store'};
    }
    // createUserNew catches and logs every transport failure; this is the only failure exit
    // after the request.
    const result = await apiService.createUserNew(
      this.formValues,
      {
        id: selectedStore.id,
        name: selectedStore.name,
      },
      !!this.formValues?.optInRewards,
      code,
      this.formValues?.signUpCode && this.formValues.signUpCode.trim() !== ''
        ? this.formValues.signUpCode
        : undefined,
    );
    if (!result.ok) {
      return result;
    }

    // DIS-1758: create-account succeeded, so the account exists. Nothing below may report failure.
    // Session stored automatically by sessionIdExtractionInterceptor before createUserNew resolves.
    if (!this.isLoggedIn) {
      logError(new Error('create-account 200 without session'), 'UserStore.signUpUser.noSession');
    }

    // The MW sends 200 only from its SUCCESS handler, so the account exists even without a body.
    if (!result.data) {
      logError(new Error('create-account 200 with empty body'), 'UserStore.signUpUser.emptyBody');
      return {ok: true};
    }

    try {
      const data = result.data;

      try {
        this.storeUserInfo(data);
      } catch (error) {
        logError(error, 'UserStore.signUpUser.storeUserInfo');
      }
      try {
        this.rootStore.invalidateLinesStores();
      } catch (error) {
        logError(error, 'UserStore.signUpUser.invalidateLines');
      }
      try {
        await this.rootStore.initialiseUserDataNonBlocking({zohoId: data.zohoId});
      } catch (error) {
        logError(error, 'UserStore.signUpUser.postCreateLoad');
      }
      if (data.rewardsUser) {
        // This won't be sent in the case of not opting in to rewards
        this.rootStore?.rewardsStore?.loadRewards(data.rewardsUser);
      } else if (this.formValues?.optInRewards) {
        /**
         * if opt in rewards is checked but zen account wasn't created. data.rewardsUser is null
         */
        runInAction(() => {
          this.loyaltyPending = true;
        });
      }

      // DIS-1537: signUpUser bypasses login()/initialiseUserData()'s store re-init,
      // so the MobX one-shot loads behind the home "What's New" carousel and the
      // "Exclusive Offers" section keep their pre-signup (guest/empty) state until the
      // app is force-killed. Re-trigger them now that the session + rewards card are set:
      // loadDepartments() reassigns rangeStore.departments so the observing
      // DepartmentCarousel re-fires loadRangeLines(), and refreshOffers() refetches offers
      // with the new card (reset() alone won't re-fire the observed onBecomeObserved).
      void this.rootStore.rangeStore.loadDepartments();
      void this.rootStore.offersStore.refreshOffers();
    } catch (error) {
      logError(error, 'UserStore.signUpUser.postCreate');
    }

    return {ok: true};
  };

  createUser = async (values: User, successCallback = () => {}, errorCallback = () => {}) => {
    try {
      runInAction(() => {
        this.showWeakPasswordCheckbox = false;
      });
      const data = (await apiService.createUser(values)) as CreateUserResponse;
      this.storeUserInfo(data);
      // Session stored automatically by sessionIdExtractionInterceptor
      this.rootStore.invalidateLinesStores();
      await this.rootStore.initialiseUserData();
      Toast.show({
        onHide: successCallback,
      });
    } catch (error) {
      errorCallback();
      this.handleCreateUpdateUserError(error);
    }
  };

  deleteUser = async (nav?: () => void) => {
    try {
      await apiService.deleteUser(this.emailAddress, this.phoneNumber, this.lastName);
      Toast.show({
        onHide: nav ? () => nav() : undefined,
      });
    } catch (err: unknown) {
      Toast.show({
        type: ERROR_MESSAGE,
        text1: err instanceof Error ? err.message : 'Something went wrong. Please try again later.',
        visibilityTime: 8000,
      });
      logError(err, 'UserStore.deleteUser');
      throw err;
    }
  };

  initiatePasswordReset = async (email: string, nav: () => void) => {
    try {
      await apiService.initiatePasswordReset({email_address: email});
      Toast.show({
        type: PASSWORD_RESET_REQUEST,
        visibilityTime: 3000,
        onHide: nav || undefined,
      });
    } catch (err) {
      logError(err, 'UserStore.initiatePasswordReset');
    }
  };

  resetPassword = async (
    {
      password,
      passwordValidationBypass = false,
    }: {password: string; passwordValidationBypass: boolean},
    token: string,
  ): Promise<{success: boolean; message?: string}> => {
    // Skip session establishment if already logged in (e.g., retry after weak password error)
    // Token is consumed on first use, so we reuse the session from previous attempt
    let userData: CreateUserResponse;

    if (!this.isLoggedIn || !this._passwordResetUserData) {
      // First attempt: establish session with token
      // Session stored automatically by sessionIdExtractionInterceptor
      try {
        await apiService.establishSessionWithToken(token);
      } catch (_err) {
        return {
          success: false,
          message: 'Invalid password reset token. Please request a new password reset.',
        };
      }

      // Get user details
      try {
        userData = await apiService.getUserInfo();
        this.storeUserInfo(userData);
        // Cache for potential retry (weak password scenario)
        this._passwordResetUserData = userData;
      } catch (_err) {
        return {
          success: false,
          message: 'Failed to update user. Please try again.',
        };
      }
    } else {
      // Retry attempt: use cached user data from first attempt
      userData = this._passwordResetUserData;
    }

    // Update user with new password
    try {
      await apiService.updateUserInfo(
        'RESET_PASSWORD',
        {
          ...userData,
          password: password,
          bypass_compromised_password_validation: passwordValidationBypass,
        },
        {
          skipConvert: true,
        },
      );
      // Clear cached data on success
      this._passwordResetUserData = null;
      return {
        success: true,
      };
    } catch (err) {
      if (err instanceof AxiosError) {
        if (err.response?.data?.status === 'PASSWORD_TOO_WEAK') {
          runInAction(() => (this.showWeakPasswordCheckbox = true));
          return {
            message: 'Please enter a strong password with a mix of letters, numbers and symbols',
            success: false,
          };
        }
        if (err.response?.data?.errors) {
          for (const error of err.response.data.errors) {
            switch (error.code.toLowerCase()) {
              case 'password_too_weak': {
                runInAction(() => (this.showWeakPasswordCheckbox = true));
                return {
                  message:
                    'Please enter a strong password with a mix of letters, numbers and symbols',
                  success: false,
                };
              }
            }
          }
        }
      }
      return {
        success: false,
        message: 'Failed to update password. Please try again.',
      };
    }
  };

  /**
   * Complete full sign up from email link
   * @param values
   * @param _successCallback - Currently unused, reserved for future use
   * @param errorCallback
   * @returns the outcome. convert-lite-user is the only password check on this
   *   path (DIS-1762), so 'password_too_weak' is what shows the bypass checkbox.
   */
  rewardsFullSignup = async (
    values: UserFormValues,
    _successCallback = () => {},
    errorCallback = () => {},
  ): Promise<'success' | 'password_too_weak' | 'error'> => {
    const billingAddress = convertAppAddressToMFLAddress(values.billingAddress);

    try {
      await apiService.convertLiteUser({
        email: this.emailAddress,
        password: values.password,
        billing_address: billingAddress,
        bypass_compromised_password_validation: values.passwordValidationBypass,
      });

      runInAction(() => {
        this.password = values.password; // needed for /verify-email endpoint
      });
      await apiService.rewardsVerifyExistingUserEmail({
        emailAddress: this.emailAddress,
      });

      navigate(AppRoute.ROOT, {
        screen: AppRoute.MAIN,
        params: {screen: AppRoute.ACCOUNT_EMAIL_VERIFICATION},
      });
      return 'success';
    } catch (error: unknown) {
      logger.error('UserStore', 'Rewards full signup error', {error});

      if ((error as AxiosError)?.response?.status === 404) {
        // unknown error (user not found in zen)
        errorCallback();
      } else {
        errorCallback();
        this.handleCreateUpdateUserError(error, true);
      }

      // The middleware answers a weak password with 422 {status: 'PASSWORD_TOO_WEAK'}.
      const body = (error as AxiosError<{status?: string}>)?.response?.data;
      return body?.status === 'PASSWORD_TOO_WEAK' ? 'password_too_weak' : 'error';
    }
  };
}
